import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { fetcher, getRequest, patchRequest, putRequest, staticOptions } from 'src/lib/core/api-client'
import { apiURI } from 'src/lib/utils/constants'
import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import { ExternalLink, Info } from 'react-feather'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import { errorToast, successToast } from 'src/lib/utils/toasters'
import { Switch, Typography, Grid } from '@mui/material'
import SaveNotification from 'src/components/common/SaveNotification'
import { useIntegrations } from 'src/lib/contexts/IntegrationsContext'
import useConfirmOnRouteChanges from 'src/lib/utils/custom-hooks/useConfirmOnRouteChanges'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import {
  defaultPopCoinsConfig,
  handleRewardsConfigErrors,
  parsePaymentModeForRewardsConfig,
  parseRewardsConfig,
  parseRewardsConfigForBackend
} from 'src/lib/utils/integrationsHelpers'
import { compareStringConvertValue } from 'src/lib/utils/helpers'
import FloCheckbox from 'src/components/common/form-elements/FloCheckbox'
import { useMerchant } from 'src/lib/contexts/MerchantContext'
import useSWR, { mutate } from 'swr'
import { PopCoinsConfig, RewardsProvider } from 'src/lib/types/integrations'
import { parsePaymentModesResponse } from 'src/lib/utils/customizeUIHelpers'
import { PaymentMethodConfigType } from 'src/lib/types/customizeUI'

const PopCoins = () => {
  const { integrations, setIntegrations } = useIntegrations()
  const { setLoaderProperties } = useLoader()
  const { t } = useLocale()
  const ISSUER_ID = process?.env?.NEXT_PUBLIC_APP_ISSUER_ID

  const {
    getters: { merchant }
  } = useMerchant()
  const [popCoinsConfig, setPopCoinsConfig] = useState<PopCoinsConfig>(defaultPopCoinsConfig)
  const [paymentModes, setPaymentModes] = useState<PaymentMethodConfigType[]>([])
  const [hasError, setHasError] = useState<boolean>(false)

  const settingsUpdated = useMemo(() => {
    return !compareStringConvertValue(integrations.popCoins, popCoinsConfig)
  }, [integrations.popCoins, popCoinsConfig])

  useEffect(() => {
    const fetchRewardsData = async () => {
      if (!merchant?.accountId || !ISSUER_ID) return

      try {
        const response: any = await getRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
          apiURI.REWARDS_ENGINE
        )
        const parsedRewardsResponse = parseRewardsConfig(response.data.data, RewardsProvider.POP_COINS)
        setPopCoinsConfig(parsedRewardsResponse)
        setIntegrations({ ...integrations, popCoins: parsedRewardsResponse })
      } catch (error) {
        console.error(error)
      }
    }

    fetchRewardsData()
  }, [merchant?.accountId, ISSUER_ID])

  useConfirmOnRouteChanges(settingsUpdated, () => {
    return new Promise((resolve: any, reject) => {
      if (confirm('You have unsaved changes. Do you want to stay on the page?')) {
        resolve(true)
      } else reject(false)
    })
  })

  const handleSave = async () => {
    if (!popCoinsConfig.metadata.authToken) {
      errorToast('Token ID is required')
      setHasError(true)
      return
    }
    const payload = parseRewardsConfigForBackend(popCoinsConfig, RewardsProvider.POP_COINS)
    const paymentModePayload = parsePaymentModeForRewardsConfig(RewardsProvider.POP_COINS, popCoinsConfig.isActive)
    try {
      setLoaderProperties({ isLoaderVisible: true })
      const promises = [
        patchRequest(
          `/issuer/${ISSUER_ID}/merchant/${merchant?.accountId}/configuration`,
          apiURI.REWARDS_ENGINE,
          payload
        ),
        putRequest(`/fms/v1/merchant-gateways/reorder-payment-modes`, apiURI.FLO_CHECKOUT, paymentModePayload)
      ]

      const [rewardsResponse, paymentModesResponse]: any = await Promise.all(promises)
      const parsedRewardsResponse = parseRewardsConfig(rewardsResponse.data.data, RewardsProvider.POP_COINS)
      setIntegrations({ ...integrations, popCoins: parsedRewardsResponse })
      setPopCoinsConfig(parsedRewardsResponse)
      if (paymentModesResponse) {
        mutate('/fms/v1/merchant-payment-modes')
      }

      successToast(t('popcoins.success'))
    } catch (e) {
      console.error(e)
      errorToast(t('popcoins.error'))
    } finally {
      setLoaderProperties({ isLoaderVisible: false })
    }
  }

  const handleDiscardSaving = () => {
    if (!integrations.popCoins) return
    setPopCoinsConfig(integrations.popCoins)
  }

  return (
    <>
      <LayoutContainer>
        <Grid item xs={12} md={12} display={'flex'} alignItems={'center'}>
          <Grid container>
            <Grid item xs={10} md={11} display={'flex'} alignItems={'center'}>
              <Typography variant='body2'>POPcoins is </Typography>
              <Typography variant='body1' sx={{ fontWeight: 500, marginLeft: 2 }}>
                {`${popCoinsConfig?.isActive ? 'enabled' : 'disabled'}`}
              </Typography>
            </Grid>
            <Grid item xs={2} md={1} display={'flex'} alignItems={'center'} justifyContent={'end'}>
              <FloCheckbox
                value={popCoinsConfig.isActive}
                setValue={(value: boolean) => {
                  setPopCoinsConfig({ ...popCoinsConfig, isActive: value })
                }}
                name='PopCoinsToggle'
                label='Enable POPcoins'
                type='SWITCH'
              />
            </Grid>
          </Grid>
        </Grid>
      </LayoutContainer>

      <LayoutContainer heading='POPcoins'>
        <div className='w-full py-3'>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-[10px] border-[1px] border-[#EFD08080] text-[#C7981F] rounded-lg w-full px-[10px] py-2 bg-[#EFD0801A]'>
              <Info size={16} color='#C7981F' />
              <p className='text-[14px]'>
                Contact the Popcoins team at{' '}
                <a href='mailto:merchant-success@popclub.co' className='font-bold' rel='noopener noreferrer'>
                  merchant-success@popclub.co
                </a>{' '}
                to get your POPcoins Token ID
              </p>
            </div>

            <InputTextField
              textFieldId='token_id'
              label='Token ID'
              isRequired={true}
              inputType='text'
              inputValue={popCoinsConfig.metadata.authToken}
              handleChange={e => {
                setPopCoinsConfig({
                  ...popCoinsConfig,
                  metadata: { ...popCoinsConfig.metadata, authToken: e.target.value }
                })
              }}
              errorStatus={hasError}
              customErrorMessage=''
              handleFocus={() => {
                setHasError(false)
              }}
            />
          </div>
        </div>
      </LayoutContainer>

      {/* <div className='mb-6 p-6 bg-white rounded-lg shadow flex flex-col gap-2'>
        <div className='w-full flex justify-between items-center'>
          <span className='text-lg font-medium'>Preview</span>
          <a
            href='https://docs.shopflo.co/docs/popcoins'
            target='_blank'
            rel='noopener noreferrer'
            className='text-[14px] font-medium text-carbon flex items-center gap-1 border-b-1 border-coal-light border-dashed'
          >
            Check documentation
            <ExternalLink size={16} />
          </a>
        </div>
        <div className='w-full'>
          <div className='flex items-center gap-2 text-gray-dark'>
            <p className='text-sm w-[60%] leading-[1.5]'>
              {t('popcoins.documentation')}
            </p>
          </div>
        </div>
      </div> */}

      <SaveNotification
        isVisible={settingsUpdated}
        onSave={handleSave}
        onDiscard={handleDiscardSaving}
        className='w-full'
      />
    </>
  )
}

export default PopCoins
